import React, { useState } from 'react';
import { Mail, Phone, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '', honeypot: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const contactInfo = [
    { icon: <Mail size={24} />, title: 'Email', detail: 'info@primeshiftgroup.com.au', link: 'mailto:info@primeshiftgroup.com.au' },
    { icon: <Phone size={24} />, title: 'Phone', detail: '(02) 8188 9656', link: 'tel:+61281889656' },
    { icon: <MapPin size={24} />, title: 'Location', detail: 'Sydney, Australia', link: null },
  ];

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/submissions/contact`, formData);
      if (res.data.success) {
        toast.success('Thank you. We will contact you shortly.');
        setFormData({ name: '', email: '', phone: '', message: '', honeypot: '' });
      }
    } catch (error) {
      if (error.response?.status === 429) toast.error('Too many submissions. Please try again later.');
      else toast.error('Something went wrong, please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white">
      <section className="bg-care-blue text-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Contact Us</h1>
          <p className="text-xl text-white/80 max-w-3xl leading-relaxed">
            Get in touch with PrimeShift Group. We respond to all enquiries within 24 hours.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {contactInfo.map((info, i) => (
              <Card key={i} className="border shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-care-light text-care-blue mb-4">
                    {info.icon}
                  </div>
                  <h3 className="font-semibold text-care-dark mb-2">{info.title}</h3>
                  {info.link ? (
                    <a href={info.link} className="text-gray-600 hover:text-care-blue transition-colors">{info.detail}</a>
                  ) : (
                    <p className="text-gray-600">{info.detail}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-care-light">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-care-dark mb-4">Send Us a Message</h2>
            <p className="text-lg text-gray-600">Complete the form below and we will respond to your enquiry.</p>
          </div>
          <Card className="shadow-xl border-none">
            <CardHeader className="bg-care-blue text-white rounded-t-lg">
              <CardTitle className="text-2xl">Contact Form</CardTitle>
            </CardHeader>
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <input type="text" name="honeypot" value={formData.honeypot} onChange={handleChange} style={{ display: 'none' }} tabIndex="-1" autoComplete="off" />
                <div>
                  <Label htmlFor="name" className="text-base">Name *</Label>
                  <Input id="name" name="name" value={formData.name} onChange={handleChange} required className="mt-2 h-12" placeholder="Your name" data-testid="contact-name-input" />
                </div>
                <div>
                  <Label htmlFor="email" className="text-base">Email *</Label>
                  <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required className="mt-2 h-12" placeholder="your.email@example.com" data-testid="contact-email-input" />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-base">Phone *</Label>
                  <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} required className="mt-2 h-12" placeholder="Contact number" data-testid="contact-phone-input" />
                </div>
                <div>
                  <Label htmlFor="message" className="text-base">Message *</Label>
                  <Textarea id="message" name="message" value={formData.message} onChange={handleChange} required className="mt-2" rows={6} placeholder="Your message..." data-testid="contact-message-input" />
                </div>
                <Button type="submit" size="lg" className="w-full bg-care-blue hover:bg-care-green text-white h-12 text-base font-semibold transition-colors duration-300" disabled={isSubmitting} data-testid="contact-submit-button">
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Contact;
